import React, { useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { SVGIcon } from '@custom-components';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { graphql } from '@apollo/client/react/hoc';
import { flowRight as compose } from 'lodash';
import withAnalytics from '../../../hocs/withAnalytics';
import { CLAIM_ADMIN_TOKEN } from './gql';
import { analyticsPropTypes, WithStylesClassesPropType } from '../../../lib/commonPropTypes';

const styles = {
  root: {
    minWidth: 0
  },
  message: {
    minWidth: 0
  },
  action: {
    paddingLeft: 8
  }
};

function OrgEditorSnackbar(props) {
  const {
    claimAdminToken,
    tokenId,
    type,
    reload,
    orgName,
    classes,
    open: defaultOpen,
    analytics,
    customerId,
    supplierId
  } = props;
  const [open, setOpen] = useState(defaultOpen);
  const [disableButtonStopEditingOrFinishEditing, setDisableButtonStopEditingOrFinishEditing] = useState(false);
  const [minimizeSnackBar, setMinimizeSnackBar] = useState(false);

  const onClose = () => {
    setDisableButtonStopEditingOrFinishEditing(true);
    claimAdminToken({ variables: { tokenId } })
      .catch(() => {
        // No Op
      })
      .finally(() => {
        setDisableButtonStopEditingOrFinishEditing(false);
        if (type === 'supplier') {
          window.close();
        }
        if (type === 'admin') {
          setOpen(false);
          reload();
        }
      });

    let orgEditorType = 'unknown';
    if (customerId) {
      orgEditorType = 'customer';
    } else if (supplierId) {
      orgEditorType = 'supplier';
    }

    analytics.trackEvent({
      event: 'track-event',
      eventName: 'closeOrgEditor',
      orgEditorType
    });
  };

  return (
    <Snackbar id="adminSnackbar" anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} open={open}>
      <SnackbarContent
        classes={open ? { root: classes.root, message: classes.message, action: classes.action } : {}}
        message={!minimizeSnackBar ? `You are currently logged in as an admin-editor for ${orgName}.` : 'Editing...'}
        action={
          minimizeSnackBar ? (
            <IconButton
              key="close"
              aria-label="Close"
              color="inherit"
              onClick={() => setMinimizeSnackBar(false)}
              size="large"
            >
              <SVGIcon selectedIcon="arrow" iconColor="white" width="14px" height="14px" rotate={270} />
            </IconButton>
          ) : (
            [
              <Button
                disabled={disableButtonStopEditingOrFinishEditing}
                key="stop"
                color="error"
                size="small"
                onClick={onClose}
              >
                {type === 'admin' ? 'STOP EDITING' : 'FINISHED EDITING'}
              </Button>,
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                onClick={() => setMinimizeSnackBar(true)}
                size="large"
              >
                <SVGIcon selectedIcon="arrow" iconColor="white" width="14px" height="14px" rotate={90} />
              </IconButton>
            ]
          )
        }
      />
    </Snackbar>
  );
}

OrgEditorSnackbar.propTypes = {
  open: PropTypes.bool,
  claimAdminToken: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['admin', 'supplier']).isRequired,
  tokenId: PropTypes.string,
  orgName: PropTypes.string.isRequired,
  reload: PropTypes.func.isRequired,
  classes: WithStylesClassesPropType.isRequired,
  analytics: analyticsPropTypes,
  customerId: PropTypes.number,
  supplierId: PropTypes.number
};
OrgEditorSnackbar.defaultProps = {
  open: false,
  tokenId: undefined,
  customerId: undefined,
  supplierId: undefined,
  analytics: undefined
};

const composedAdminSnackbar = compose(
  withAnalytics(),
  graphql(CLAIM_ADMIN_TOKEN, { name: 'claimAdminToken' }),
  withStyles(styles)
)(OrgEditorSnackbar);

export default composedAdminSnackbar;
